import { supabase } from '@/lib/supabase-client';
/**
 * Agent API Service
 * Handles all API calls for agents with fallback to mock data
 */
const API_ENABLED = false; // Toggle to switch between API and mock data
class AgentService {
    mockAgents = [];
    mockTemplates = [];
    mockTeams = [];
    mockAssignments = [];
    constructor() {
        this.initializeMockData();
    }
    initializeMockData() {
        // Initialize with empty arrays - will be populated by the store
        this.mockAgents = [];
        this.mockTemplates = [];
        this.mockTeams = [];
        this.mockAssignments = [];
    }
    /**
     * Get all agents
     */
    async getAgents() {
        if (!API_ENABLED) {
            // Return mock data or empty array
            return Promise.resolve(this.mockAgents);
        }
        try {
            const { data, error } = await supabase
                .from('agents')
                .select('*')
                .order('created_at', { ascending: false });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching agents:', error);
            // Fallback to mock data
            return this.mockAgents;
        }
    }
    /**
     * Get agent by ID
     */
    async getAgentById(id) {
        if (!API_ENABLED) {
            return Promise.resolve(this.mockAgents.find(a => a.id === id) || null);
        }
        try {
            const { data, error } = await supabase
                .from('agents')
                .select('*')
                .eq('id', id)
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error fetching agent:', error);
            return this.mockAgents.find(a => a.id === id) || null;
        }
    }
    /**
     * Create new agent
     */
    async createAgent(agentData) {
        if (!API_ENABLED) {
            const newAgent = {
                id: `agent-${Date.now()}`,
                name: agentData.name,
                description: agentData.description,
                type: agentData.type,
                status: 'inactive',
                instructions: agentData.instructions.map((inst, index) => ({
                    ...inst,
                    id: `inst-${Date.now()}-${index}`
                })),
                guardrails: agentData.guardrails.map((guard, index) => ({
                    ...guard,
                    id: `guard-${Date.now()}-${index}`
                })),
                skills: [],
                capabilities: agentData.capabilities,
                managerId: agentData.managerId,
                teamIds: agentData.teamIds || [],
                availability: agentData.availability,
                metrics: {
                    tasksCompleted: 0,
                    tasksInProgress: 0,
                    successRate: 0,
                    averageCompletionTime: 0,
                    escalationsToHuman: 0,
                    costSavings: 0,
                    lastActiveAt: new Date().toISOString(),
                    totalRuntime: 0
                },
                integrations: {},
                version: '1.0.0',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                createdBy: 'current-user',
                tags: agentData.tags || [],
                modelConfig: {
                    model: 'gpt-4',
                    temperature: 0.7,
                    maxTokens: 2000,
                    systemPrompt: `You are ${agentData.name}. ${agentData.description}`,
                    ...agentData.modelConfig
                }
            };
            this.mockAgents.push(newAgent);
            return Promise.resolve(newAgent);
        }
        try {
            const { data, error } = await supabase
                .from('agents')
                .insert([agentData])
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error creating agent:', error);
            throw error;
        }
    }
    /**
     * Update agent
     */
    async updateAgent(id, updates) {
        if (!API_ENABLED) {
            const index = this.mockAgents.findIndex(a => a.id === id);
            if (index === -1) {
                throw new Error('Agent not found');
            }
            this.mockAgents[index] = {
                ...this.mockAgents[index],
                ...updates,
                updatedAt: new Date().toISOString()
            };
            return Promise.resolve(this.mockAgents[index]);
        }
        try {
            const { data, error } = await supabase
                .from('agents')
                .update({ ...updates, updated_at: new Date().toISOString() })
                .eq('id', id)
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error updating agent:', error);
            throw error;
        }
    }
    /**
     * Delete agent
     */
    async deleteAgent(id) {
        if (!API_ENABLED) {
            this.mockAgents = this.mockAgents.filter(a => a.id !== id);
            return Promise.resolve();
        }
        try {
            const { error } = await supabase
                .from('agents')
                .delete()
                .eq('id', id);
            if (error)
                throw error;
        }
        catch (error) {
            console.error('Error deleting agent:', error);
            throw error;
        }
    }
    /**
     * Toggle agent status (activate/deactivate)
     */
    async toggleAgentStatus(id, status) {
        return this.updateAgent(id, { status });
    }
    /**
     * Get agent templates
     */
    async getAgentTemplates() {
        if (!API_ENABLED) {
            return Promise.resolve(this.mockTemplates);
        }
        try {
            const { data, error } = await supabase
                .from('agent_templates')
                .select('*');
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching agent templates:', error);
            return this.mockTemplates;
        }
    }
    /**
     * Get agent teams
     */
    async getAgentTeams() {
        if (!API_ENABLED) {
            return Promise.resolve(this.mockTeams);
        }
        try {
            const { data, error } = await supabase
                .from('agent_teams')
                .select('*');
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching agent teams:', error);
            return this.mockTeams;
        }
    }
    /**
     * Get agent assignments
     */
    async getAgentAssignments(agentId) {
        if (!API_ENABLED) {
            if (agentId) {
                return Promise.resolve(this.mockAssignments.filter(a => a.agentId === agentId));
            }
            return Promise.resolve(this.mockAssignments);
        }
        try {
            let query = supabase.from('agent_assignments').select('*');
            if (agentId) {
                query = query.eq('agent_id', agentId);
            }
            const { data, error } = await query;
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching agent assignments:', error);
            return this.mockAssignments;
        }
    }
    /**
     * Set mock data (for development/testing)
     */
    setMockData(agents, templates, teams, assignments) {
        this.mockAgents = agents;
        this.mockTemplates = templates;
        this.mockTeams = teams;
        this.mockAssignments = assignments;
    }
    /**
     * Check if API is enabled
     */
    isApiEnabled() {
        return API_ENABLED;
    }
}
// Export singleton instance
export const agentService = new AgentService();
